/**
 * Mock data storage and routing
 */

import { authMockData } from '@/mocks/data/auth.mock';
import { productsMockData } from '@/mocks/data/products.mock';
import { customerOperations } from '@/mocks/data/customers/base';
import { apiKeyOperations } from '@/mocks/data/customers/api-keys';
import { checkoutLinkOperations } from '@/mocks/data/customers/checkout-links';
import { subscriptionOperations } from '@/mocks/data/customers/subscriptions';
import { teamOperations } from '@/mocks/data/customers/teams';
import { providerTeamOperations } from '@/mocks/data/teams/base';
import { customerPlanManagementOperations } from '@/mocks/data/customers/plan-management';
import { customerInvoiceOperations } from '@/mocks/data/customers/invoices';

/**
 * Route mock data requests to appropriate handlers
 */
export function getMockData(
  method: string,
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  // Normalize URL - remove query parameters and leading/trailing slashes
  const cleanUrl = normalizeUrl(url);
  
  // Route to appropriate mock data handler
  const mockHandler = findMockHandler(method, cleanUrl);
  
  if (!mockHandler) {
    throw {
      response: {
        status: 404,
        data: {
          message: `Mock endpoint not found: ${method} ${cleanUrl}`,
          status: 404
        }
      }
    };
  }
  
  // Handle query parameters for GET requests
  const queryParams = method === 'GET' ? extractQueryParams(url) : {};
  const requestData = { ...data, ...queryParams };
  
  // Execute mock handler
  return mockHandler(requestData, config);
}

/**
 * Find mock handler for the given method and URL
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findMockHandler(method: string, url: string): ((data?: any, config?: any) => any) | null {
  // Auth endpoints
  if (url.startsWith('/api/v1/auth') || url.startsWith('/api/auth')) {
    const authHandler = authMockData[url as keyof typeof authMockData];
    if (authHandler) return authHandler;
  }

  if (url === '/api/v1/customer/portal/menu' && method === 'GET') {
    return () => ({ items: [] });
  }

  if (url === '/api/v1/customer/plan-management' && method === 'GET') {
    return () => customerPlanManagementOperations.getPlanManagement();
  }

  if (url === '/api/v1/customer/subscription/checkout-context' && method === 'GET') {
    return () => customerPlanManagementOperations.getCheckoutContext();
  }

  if (url === '/api/v1/customer/subscription/checkout-intent' && method === 'POST') {
    return () => customerPlanManagementOperations.createCheckoutIntent();
  }

  if (url === '/api/v1/customer/subscription/checkout-confirm' && method === 'POST') {
    return (data) => customerPlanManagementOperations.confirmCheckout(data);
  }

  if (url === '/api/v1/customer/subscription/upgrade' && method === 'POST') {
    return (data) => customerPlanManagementOperations.upgrade(data);
  }

  if (url === '/api/v1/customer/subscription/downgrade' && method === 'POST') {
    return (data) => customerPlanManagementOperations.downgrade(data);
  }

  if (url === '/api/v1/customer/subscription/cancel' && method === 'POST') {
    return () => customerPlanManagementOperations.cancel();
  }

  if (url === '/api/v1/customer/subscription/downgrade-options' && method === 'GET') {
    return () => customerPlanManagementOperations.getDowngradeOptions();
  }

  if (url === '/api/v1/customer/invoices' && method === 'GET') {
    return (_data, config) => customerInvoiceOperations.listInvoices(config?.params);
  }
  
  // Products endpoints
  if (
    url.startsWith('/products') ||
    url.startsWith('/api/v1/products') ||
    url.startsWith('/api/products') ||
    url.startsWith('/provider/products')
  ) {
    // Handle parameterized routes
    const productHandler = findProductHandler(method, url);
    if (productHandler) return productHandler;
  }
  
  // Customers endpoints
  if (
    url.startsWith('/customers') ||
    url.startsWith('/api/v1/customers') ||
    url.startsWith('/api/customers')
  ) {
    const customerHandler = findCustomerHandler(method, url);
    if (customerHandler) return customerHandler;
  }

  if (url.match(/^\/api\/teams\/[^/]+\/memberships\/[^/]+$/) && method === 'DELETE') {
    const cleanUrl = url.replace(/^\/api/, '');
    const [, , teamId, , memberId] = cleanUrl.split('/');
    return () => teamOperations.removeMemberByTeamId(teamId, memberId);
  }

  // Provider teams endpoints
  if (
    url.startsWith('/teams') ||
    url.startsWith('/api/v1/teams') ||
    url.startsWith('/api/teams')
  ) {
    const teamHandler = findProviderTeamHandler(method, url);
    if (teamHandler) return teamHandler;
  }
  
  return null;
}

/**
 * Find handler for customers endpoints with parameter support
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findCustomerHandler(method: string, url: string): ((data?: any, config?: any) => any) | null {
  const cleanUrl = url.replace(/^\/api(?:\/v1)?/, ''); // Remove /api or /api/v1 prefix
  
  // Base customers endpoints
  if (cleanUrl === '/customers' && method === 'GET') {
    return (data) => customerOperations.list(data);
  }
  
  if (cleanUrl === '/customers' && method === 'POST') {
    return (data) => customerOperations.create(data);
  }
  
  // Single customer: /customers/:id
  if (cleanUrl.match(/^\/customers\/[^/]+$/)) {
    const id = cleanUrl.split('/')[2];
    
    if (method === 'GET') {
      return () => customerOperations.getById(id);
    }
    if (method === 'PUT' || method === 'PATCH') {
      return (data) => customerOperations.update(id, data);
    }
    if (method === 'DELETE') {
      return () => customerOperations.delete(id);
    }
  }
  
  // API Keys endpoints: /customers/:id/api-keys
  if (cleanUrl.match(/^\/customers\/[^/]+\/api-keys$/)) {
    const customerId = cleanUrl.split('/')[2];
    
    if (method === 'GET') {
      return () => apiKeyOperations.list(customerId);
    }
    if (method === 'POST') {
      return (data) => apiKeyOperations.create(customerId, data);
    }
  }
  
  // API Key operations: /customers/:customerId/api-keys/:keyId
  if (cleanUrl.match(/^\/customers\/[^/]+\/api-keys\/[^/]+$/)) {
    const [, , customerId, , keyId] = cleanUrl.split('/');
    
    if (method === 'PUT' || method === 'PATCH') {
      return (data) => apiKeyOperations.update(customerId, keyId, data);
    }
    if (method === 'DELETE') {
      return () => apiKeyOperations.delete(customerId, keyId);
    }
  }
  
  // API Key regenerate: /customers/:customerId/api-keys/:keyId/regenerate
  if (cleanUrl.match(/^\/customers\/[^/]+\/api-keys\/[^/]+\/regenerate$/)) {
    const [, , customerId, , keyId] = cleanUrl.split('/');
    
    if (method === 'POST' || method === 'PUT') {
      return () => apiKeyOperations.regenerate(customerId, keyId);
    }
  }

  // Checkout links endpoints: /customers/:id/checkout-links
  if (cleanUrl.match(/^\/customers\/[^/]+\/checkout-links$/)) {
    const customerId = cleanUrl.split('/')[2];

    if (method === 'GET') {
      return () => checkoutLinkOperations.list(customerId);
    }
  }

  // Checkout link options: /customers/:id/checkout-link-options
  if (cleanUrl.match(/^\/customers\/[^/]+\/checkout-link-options$/)) {
    const customerId = cleanUrl.split('/')[2];

    if (method === 'GET') {
      return () => checkoutLinkOperations.options(customerId);
    }
  }

  // Checkout link generation: /customers/:id/checkout-link
  if (cleanUrl.match(/^\/customers\/[^/]+\/checkout-link$/)) {
    const customerId = cleanUrl.split('/')[2];

    if (method === 'POST') {
      return (data) => checkoutLinkOperations.create(customerId, data);
    }
  }
  
  // Subscriptions endpoints: /customers/:id/subscriptions
  if (cleanUrl.match(/^\/customers\/[^/]+\/subscriptions$/)) {
    const customerId = cleanUrl.split('/')[2];
    
    if (method === 'GET') {
      return () => subscriptionOperations.list(customerId);
    }
    if (method === 'POST') {
      return (data) => subscriptionOperations.create(customerId, data);
    }
  }
  
  // Single subscription: /customers/:customerId/subscriptions/:subscriptionId
  if (cleanUrl.match(/^\/customers\/[^/]+\/subscriptions\/[^/]+$/)) {
    const [, , customerId, , subscriptionId] = cleanUrl.split('/');
    
    if (method === 'GET') {
      return () => subscriptionOperations.getById(customerId, subscriptionId);
    }
    if (method === 'PUT' || method === 'PATCH') {
      return (data) => subscriptionOperations.update(customerId, subscriptionId, data);
    }
    if (method === 'DELETE') {
      return () => subscriptionOperations.delete(customerId, subscriptionId);
    }
  }
  
  // Subscription actions: /customers/:customerId/subscriptions/:subscriptionId/:action
  if (cleanUrl.match(/^\/customers\/[^/]+\/subscriptions\/[^/]+\/(cancel|reactivate)$/)) {
    const [, , customerId, , subscriptionId, action] = cleanUrl.split('/');
    
    if (method === 'POST' || method === 'PUT') {
      if (action === 'cancel') {
        return () => subscriptionOperations.cancel(customerId, subscriptionId);
      }
      if (action === 'reactivate') {
        return () => subscriptionOperations.reactivate(customerId, subscriptionId);
      }
    }
  }
  
  // Teams endpoints: /customers/:id/teams
  if (cleanUrl.match(/^\/customers\/[^/]+\/teams$/)) {
    const customerId = cleanUrl.split('/')[2];
    
    if (method === 'GET') {
      return () => teamOperations.list(customerId);
    }
    if (method === 'POST') {
      return (data) => teamOperations.create(customerId, data);
    }
  }

  // Teams dashboard: /customers/:id/teams/dashboard
  if (cleanUrl.match(/^\/customers\/[^/]+\/teams\/dashboard$/) && method === 'GET') {
    const customerId = cleanUrl.split('/')[2];
    return () => teamOperations.dashboard(customerId);
  }
  
  // Single team: /customers/:customerId/teams/:teamId
  if (cleanUrl.match(/^\/customers\/[^/]+\/teams\/[^/]+$/)) {
    const [, , customerId, , teamId] = cleanUrl.split('/');
    
    if (method === 'GET') {
      return () => teamOperations.getById(customerId, teamId);
    }
    if (method === 'PUT' || method === 'PATCH') {
      return (data) => teamOperations.update(customerId, teamId, data);
    }
    if (method === 'DELETE') {
      return () => teamOperations.delete(customerId, teamId);
    }
  }
  
  // Team actions: leave or create member/invite
  if (cleanUrl.match(/^\/customers\/[^/]+\/teams\/[^/]+\/(leave|members|invitations)(?:\/[^/]+)?(?:\/resend)?$/)) {
    const parts = cleanUrl.split('/');
    const customerId = parts[2];
    const teamId = parts[4];
    const action = parts[5];
    const subAction = parts[6];
    const extra = parts[7];

    if (action === 'leave' && method === 'POST') {
      return () => teamOperations.leave(customerId, teamId);
    }

    if (action === 'members') {
      if (method === 'POST') {
        return (data) => teamOperations.addMember(customerId, teamId, data);
      }
      if (subAction && method === 'DELETE') {
        return () => teamOperations.removeMember(customerId, teamId, subAction);
      }
      if (subAction && method === 'PATCH') {
        return (data) => teamOperations.updateMemberRole(customerId, teamId, subAction, data);
      }
    }

    if (action === 'invitations') {
      if (!subAction && method === 'POST') {
        return (data) => teamOperations.addMember(customerId, teamId, data);
      }
      if (subAction && extra === 'resend' && method === 'POST') {
        return () => teamOperations.resendInvitation(customerId, teamId, subAction);
      }
      if (subAction && !extra && method === 'DELETE') {
        return () => teamOperations.cancelInvitation(customerId, teamId, subAction);
      }
    }
  }
  
  return null;
}

function findProviderTeamHandler(method: string, url: string): ((data?: unknown, config?: unknown) => unknown) | null {
  const cleanUrl = url.replace(/^\/api(?:\/v1)?/, '');

  if (cleanUrl === '/teams' && method === 'GET') {
    return () => providerTeamOperations.list();
  }

  return null;
}

/**
 * Find handler for products endpoints with parameter support
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findProductHandler(method: string, url: string): ((data?: any, config?: any) => any) | null {
  // Remove prefixes and normalize to /products
  const cleanUrl = url.replace(/^\/api(?:\/v1)?/, '').replace(/^\/provider/, '');
  
  // Exact matches first
  if (productsMockData[cleanUrl as keyof typeof productsMockData]) {
    return productsMockData[cleanUrl as keyof typeof productsMockData];
  }
  
  // Handle parameterized routes
  if (cleanUrl.match(/^\/products\/[^/]+$/)) {
    // Single product: /products/:id
    const id = cleanUrl.split('/')[2];
    return () => productsMockData['/products/:id'](id);
  }
  
  if (cleanUrl.match(/^\/products\/[^/]+\/[^/]+$/)) {
    // Product sub-resources: /products/:id/action
    const [, , id, action] = cleanUrl.split('/');
    const key = `/products/:id/${action}` as keyof typeof productsMockData;
    if (productsMockData[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data) => (productsMockData[key] as any)(id, data);
    }
  }
  
  // Handle HTTP methods
  if (method === 'POST' && cleanUrl === '/products') {
    return productsMockData['/products/create'];
  }
  
  if (method === 'PUT' && cleanUrl.match(/^\/products\/[^/]+$/)) {
    const id = cleanUrl.split('/')[2];
    return (data) => productsMockData['/products/:id/update'](id, data);
  }
  
  if (method === 'DELETE' && cleanUrl.match(/^\/products\/[^/]+$/)) {
    const id = cleanUrl.split('/')[2];
    return () => productsMockData['/products/:id/delete'](id);
  }
  
  return null;
}

/**
 * Normalize URL by removing query params and cleaning up
 */
function normalizeUrl(url: string): string {
  const [path] = url.split('?');
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Extract query parameters from URL
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractQueryParams(url: string): Record<string, any> {
  const [, queryString] = url.split('?');
  if (!queryString) return {};
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: Record<string, any> = {};
  const pairs = queryString.split('&');
  
  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key && value !== undefined) {
      const decodedKey = decodeURIComponent(key);
      const decodedValue = decodeURIComponent(value);
      
      // Try to parse numbers and booleans
      if (decodedValue === 'true') {
        params[decodedKey] = true;
      } else if (decodedValue === 'false') {
        params[decodedKey] = false;
      } else if (/^\d+$/.test(decodedValue)) {
        params[decodedKey] = parseInt(decodedValue, 10);
      } else {
        params[decodedKey] = decodedValue;
      }
    }
  }
  
  return params;
}
